import React, { useEffect, useState } from 'react';
import { cancelOrder, getMyOrders } from '../api';

const badge = (status) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
    background: status === 'completed' ? '#d1fae5' : status === 'processing' ? '#dbeafe' : status === 'pending' ? '#fef3c7' : status === 'canceled' ? '#f3f4f6' : status === 'partial' ? '#fef3c7' : '#fee2e2',
    color: status === 'completed' ? '#065f46' : status === 'processing' ? '#1d4ed8' : status === 'pending' ? '#92400e' : status === 'canceled' ? '#6b7280' : status === 'partial' ? '#92400e' : '#991b1b',
});


const s = {
    h1:    { fontSize: 22, fontWeight: 700, marginBottom: 20 },
    h2:    { fontSize: 17, fontWeight: 700, marginBottom: 14, marginTop: 28, color: '#374151' },
    panel: { background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,.06)', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 12, tableLayout: 'fixed' },
    th:    { textAlign: 'left', padding: '8px 10px', background: '#f3f4f6', borderBottom: '2px solid #d1d5db', color: '#4b5563', fontSize: 11, textTransform: 'uppercase', letterSpacing: .3, whiteSpace: 'nowrap', fontWeight: 700 },
    td:    { padding: '8px 10px', borderBottom: '1px solid #e5e7eb', verticalAlign: 'middle' },
    btn:   { padding: '4px 10px', background: 'none', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 12, color: '#6b7280' },
    filter:{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' },
    sel:   { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: '#fff' },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 },
    dialog: { background: '#fff', borderRadius: 14, padding: '28px 32px', maxWidth: 440, width: '90%', boxShadow: '0 16px 48px rgba(0,0,0,0.2)' },
    dTitle: { fontSize: 18, fontWeight: 700, color: '#1a1d23', marginBottom: 16 },
    dRule: { fontSize: 13, color: '#374151', lineHeight: 1.7, marginBottom: 6 },
    dWarn: { fontSize: 12, color: '#dc2626', fontWeight: 600, marginTop: 12, marginBottom: 16 },
    dBtnRow: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 },
    dBtnCancel: { padding: '10px 20px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' },
    dBtnConfirm: { padding: '10px 20px', border: 'none', borderRadius: 8, background: '#dc2626', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
};

const STATUSES = ['all', 'pending', 'processing', 'completed', 'partial', 'canceled', 'error'];

const isSubscriptionOrder = (o) => {
    return o.category === 'Subscription' || (o.service_name || '').toLowerCase().includes('subscription');
};

const getRefundPreview = (o) => {
    if (isSubscriptionOrder(o)) {
        // Subscription: 24h grace period only
        const hoursSince = (Date.now() - new Date(o.created_at)) / 3600000;
        if (hoursSince <= 24) {
            return { percent: 100, amount: parseFloat(o.price), note: 'Within 24-hour grace period' };
        }
        // After 24h — no cancel possible (button hidden anyway)
        return { percent: 0, amount: 0, note: 'Grace period expired' };
    } else {
        // SMM: progress-based rules
        const remains = o.remains ?? o.quantity;
        const progress = o.quantity > 0 ? (o.quantity - remains) / o.quantity : 0;

        if (progress === 0) {
            return { percent: 100, amount: parseFloat(o.price), note: 'Order not started' };
        } else if (progress < 0.5) {
            return { percent: 50, amount: parseFloat((o.price * 0.5).toFixed(4)), note: `${Math.round(progress * 100)}% delivered` };
        } else {
            return { percent: 0, amount: 0, note: `${Math.round(progress * 100)}% delivered (over 50%)` };
        }
    }
};

function OrderTable({ orders, showClient, onCancelClick }) {
    if (orders.length === 0) {
        return <p style={{ padding: 24, color: '#9ca3af' }}>No orders found.</p>;
    }

    return (
        <div className="og-table-wrap">
        <table style={s.table}>
            <colgroup>
                <col style={{ width: 60 }} />{/* # id */}
                {showClient && <col style={{ width: 90 }} />}
                <col style={{ width: 90 }} />{/* Category */}
                <col style={{ width: 180 }} />{/* Service */}
                <col style={{ width: 65 }} />{/* Svc ID */}
                <col style={{ width: 180 }} />{/* Link */}
                <col style={{ width: 55 }} />{/* Qty */}
                <col style={{ width: 105 }} />{/* Status */}
                <col style={{ width: 150 }} />{/* Progress */}
                <col style={{ width: 80 }} />{/* Price */}
                <col style={{ width: 105 }} />{/* Created */}
                <col style={{ width: 75 }} />{/* Actions */}
            </colgroup>
            <thead>
                <tr>
                    {['#', ...(showClient ? ['Client'] : []), 'Category', 'Service', 'Svc ID', 'Link', 'Qty', 'Status', 'Progress', 'Price', 'Created', 'Actions'].map(h => (
                        <th key={h} style={s.th}>{h}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {orders.map((o, rowIdx) => {
                    const isSub = isSubscriptionOrder(o);
                    const remains = o.remains ?? o.quantity;
                    const delivered = o.quantity - remains;
                    const pct = o.quantity > 0 ? Math.round((delivered / o.quantity) * 100) : 0;
                    const target = o.start_count ? o.start_count + o.quantity : null;

                    return (
                        <tr key={o.id} style={rowIdx % 2 === 0 ? undefined : { background: '#fafbfc' }}>
                            <td style={s.td}>{o.id}</td>
                            {showClient && <td style={{ ...s.td, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={o.username || `#${o.client_id}`}>{o.username || `#${o.client_id}`}</td>}
                            <td style={{ ...s.td, fontSize: 11, color: '#6b7280' }}>
                                {o.category || o.service_category || '-'}
                            </td>
                            <td style={{ ...s.td, maxWidth: 140, wordBreak: 'break-word' }}>
                                {o.service_name?.replace(/^.*?—\s*/, '') || o.service_id}
                            </td>
                            <td style={{ ...s.td, fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap' }}>
                                {o.service_id || '-'}
                            </td>
                            <td style={{ ...s.td, maxWidth: 150, wordBreak: 'break-all' }}>
                                {o.link ? (
                                    <a
                                        href={o.link.startsWith('http') ? o.link : `https://instagram.com/${o.link}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: '#4f46e5', textDecoration: 'none', fontSize: 11 }}
                                    >
                                        {o.link} <span style={{ fontSize: 10 }}>&#x2197;</span>
                                    </a>
                                ) : '-'}
                            </td>
                            <td style={s.td}>{o.quantity}</td>
                            <td style={s.td} title={['canceled', 'partial'].includes(o.status) && o.cancel_note ? `${o.refund_amount > 0 ? '€' + parseFloat(o.refund_amount).toFixed(2) + ' — ' : ''}${o.cancel_note}` : ''}>
                                <span style={badge(o.status)}>
                                    {o.status}
                                </span>
                                {['canceled', 'partial'].includes(o.status) && (o.cancel_note || o.refund_amount > 0) && (
                                    <div style={{ fontSize: 10, color: '#6b7280', marginTop: 3, lineHeight: 1.3, maxWidth: 140, wordBreak: 'break-word' }}>
                                        {o.refund_amount > 0 && (
                                            <span style={{ color: '#059669', fontWeight: 600 }}>€{parseFloat(o.refund_amount).toFixed(2)}</span>
                                        )}
                                        {o.refund_amount > 0 && o.cancel_note && ' — '}
                                        {o.cancel_note}
                                    </div>
                                )}
                            </td>
                            <td style={{ ...s.td, minWidth: 110 }}>
                                {isSub ? (
                                    <div>
                                        {(() => {
                                            const hoursSince = (Date.now() - new Date(o.created_at)) / 3600000;
                                            const inGracePeriod = hoursSince < 24 && ['pending', 'processing'].includes(o.status);

                                            // Grace period: only show "Starts in Xh", no day counter
                                            if (inGracePeriod) {
                                                return (
                                                    <div style={{ fontSize: 12, color: '#d97706', fontWeight: 600 }}>
                                                        Starts in {Math.ceil(24 - hoursSince)}h
                                                    </div>
                                                );
                                            }

                                            // Active/expired: show Day X/Y progress bar
                                            if (o.subscription_end_date) {
                                                const start = new Date(o.subscription_start_date || o.created_at);
                                                const end = new Date(o.subscription_end_date);
                                                const now = new Date();
                                                // end_date is exclusive (start + duration)
                                                const totalDays = Math.max(1, Math.round((end - start) / 86400000));
                                                const elapsed = Math.min(totalDays, Math.max(1, Math.floor((now - start) / 86400000) + 1));
                                                const daysPct = Math.min(100, Math.round((elapsed / totalDays) * 100));
                                                const isExpired = now > end;
                                                return (
                                                    <>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#374151', marginBottom: 3 }}>
                                                            <span style={{ fontWeight: 600 }}>Day {elapsed}/{totalDays}</span>
                                                            <span style={{ color: isExpired ? '#dc2626' : '#9ca3af' }}>
                                                                {isExpired ? 'Expired' : `Ends ${new Date(end.getTime() - 86400000).toLocaleDateString()}`}
                                                            </span>
                                                        </div>
                                                        <div style={{ background: '#e5e7eb', borderRadius: 3, height: 5, overflow: 'hidden' }}>
                                                            <div style={{ background: isExpired ? '#dc2626' : '#8b5cf6', height: '100%', width: `${daysPct}%`, borderRadius: 3, transition: 'width .3s' }} />
                                                        </div>
                                                    </>
                                                );
                                            }

                                            return <div style={{ fontSize: 12, color: '#6b7280' }}>Subscription</div>;
                                        })()}
                                    </div>
                                ) : (
                                    <div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#374151', marginBottom: 3 }}>
                                            <span style={{ fontWeight: 600 }}>{pct}%</span>
                                            <span style={{ color: '#9ca3af' }}>{delivered}/{o.quantity}</span>
                                        </div>
                                        <div style={{ background: '#e5e7eb', borderRadius: 3, height: 5, overflow: 'hidden' }}>
                                            <div style={{ background: pct >= 100 ? '#22c55e' : '#6366f1', height: '100%', width: `${Math.min(pct, 100)}%`, borderRadius: 3, transition: 'width .3s' }} />
                                        </div>
                                        {target && (
                                            <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }} title="Start count available for: Follow, Like, Comment services only">
                                                Start: {o.start_count} &rarr; Target: {target}
                                            </div>
                                        )}
                                        {o.last_action_at && ['processing', 'pending'].includes(o.status) && (
                                            <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
                                                Last update: {new Date(o.last_action_at).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </td>
                            <td style={s.td}>&euro;{parseFloat(o.price).toFixed(4)}</td>
                            <td style={s.td}>{new Date(o.created_at).toLocaleDateString('en-GB', { day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit' })}</td>
                            <td style={s.td}>
                                {['pending', 'processing'].includes(o.status) && (() => {
                                    if (!isSub) {
                                        const progress = o.quantity > 0 ? (o.quantity - (o.remains ?? o.quantity)) / o.quantity : 0;
                                        if (progress >= 0.5) {
                                            return (
                                                <span style={{ fontSize: 11, color: '#9ca3af', cursor: 'default' }} title="Over 50% delivered — cancellation not available per Terms of Service">
                                                    Cancel N/A
                                                </span>
                                            );
                                        }
                                    } else {
                                        // Subscription: cancel only within 24h grace period
                                        const hoursSince = (Date.now() - new Date(o.created_at)) / 3600000;
                                        if (hoursSince > 24) {
                                            return (
                                                <span style={{ fontSize: 11, color: '#9ca3af', cursor: 'default' }} title="Subscription active — cancellation not available after 24-hour grace period">
                                                    Cancel N/A
                                                </span>
                                            );
                                        }
                                    }
                                    return <button style={s.btn} onClick={() => onCancelClick(o)}>Cancel</button>;
                                })()}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
        </div>
    );
}

export default function Orders() {
    const [orders, setOrders] = useState([]);
    const [status, setStatus] = useState('all');
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [cancelDialog, setCancelDialog] = useState(null);

    const load = (st) => {
        setLoading(true);
        const params = { limit: 200 };
        if (st !== 'all') params.status = st;
        getMyOrders(params)
            .then(r => setOrders(Array.isArray(r.data) ? r.data : []))
            .catch(() => setOrders([]))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(status); }, [status]); // eslint-disable-line

    const openCancelDialog = (order) => setCancelDialog(order);

    const doCancel = async () => {
        if (!cancelDialog) return;
        try {
            await cancelOrder(cancelDialog.id);
            setCancelDialog(null);
            load(status);
        } catch (e) {
            alert(e.response?.data?.error || 'Cancel failed');
        }
    };

    const sq = search.toLowerCase();
    const displayed = sq
        ? orders.filter(o =>
            o.link?.toLowerCase().includes(sq) ||
            o.service_name?.toLowerCase().includes(sq) ||
            o.category?.toLowerCase().includes(sq) ||
            String(o.id).includes(sq))
        : orders;

    const isSub = cancelDialog ? isSubscriptionOrder(cancelDialog) : false;

    return (
        <div>
            <h1 style={s.h1}>My Orders</h1>
            <div className="og-filters" style={{ ...s.filter, flexWrap: 'wrap' }}>
                <select style={s.sel} value={status} onChange={e => setStatus(e.target.value)}>
                    {STATUSES.map(st => <option key={st} value={st}>{st.charAt(0).toUpperCase() + st.slice(1)}</option>)}
                </select>
                <input
                    style={{ ...s.sel, minWidth: 220 }}
                    type="text"
                    placeholder="Search by link, service, order ID..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                {search && <span style={{ fontSize: 12, color: '#6b7280' }}>{displayed.length} results</span>}
            </div>

            {loading ? (
                <p style={{ padding: 24, color: '#9ca3af' }}>Loading...</p>
            ) : (
                <div style={s.panel}>
                    <OrderTable orders={displayed} showClient={false} onCancelClick={openCancelDialog} />
                </div>
            )}

            {/* Cancel confirmation dialog */}
            {cancelDialog && (
                <div style={s.overlay} onClick={() => setCancelDialog(null)}>
                    <div style={s.dialog} onClick={e => e.stopPropagation()}>
                        <div style={s.dTitle}>Cancel Order #{cancelDialog.id}</div>
                        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
                            Our cancellation policy:
                        </div>
                        {isSub ? (
                            <>
                                <div style={s.dRule}>&#8226; Within 24 hours of purchase: <strong>100% refund</strong> (no work started)</div>
                                <div style={s.dRule}>&#8226; After 24 hours: subscription is active and <strong>cannot be canceled</strong></div>
                            </>
                        ) : (
                            <>
                                <div style={s.dRule}>&#8226; Not started (0% delivered): <strong>100% refund</strong></div>
                                <div style={s.dRule}>&#8226; Less than 50% delivered: <strong>50% refund</strong></div>
                                <div style={s.dRule}>&#8226; 50% or more delivered: <strong>no refund</strong> (work stops)</div>
                            </>
                        )}
                        <div style={{ borderTop: '1px solid #f3f4f6', margin: '16px 0', paddingTop: 12 }}>
                            {(() => {
                                const preview = getRefundPreview(cancelDialog);
                                return (
                                    <div style={{ fontSize: 14 }}>
                                        <span style={{ color: '#374151', fontWeight: 600 }}>Your estimated refund: </span>
                                        <span style={{ color: preview.percent > 0 ? '#059669' : '#dc2626', fontWeight: 700, fontSize: 16 }}>
                                            &euro;{preview.amount.toFixed(2)} ({preview.percent}%)
                                        </span>
                                        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>{preview.note}</div>
                                    </div>
                                );
                            })()}
                        </div>
                        {getRefundPreview(cancelDialog).percent === 0 && (
                            <div style={s.dWarn}>
                                {isSub
                                    ? 'This subscription is past the refund window. You will not receive a refund.'
                                    : 'This order is over 50% delivered. You will not receive a refund.'}
                            </div>
                        )}
                        <div style={s.dBtnRow}>
                            <button style={s.dBtnCancel} onClick={() => setCancelDialog(null)}>Keep Order</button>
                            <button style={s.dBtnConfirm} onClick={doCancel}>Cancel Order</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
